import * as React from "react";

import classNames from "classnames/bind";
import UIIcon from "common/blocks/UIIcon";

import DefaultErrorView from "../TextInput/DefaultErrorView";
import styles from "./DropdownInput.module.scss";

const cx = classNames.bind(styles);

export interface Option {
    value: string;
    label: string;
}

export interface GroupedOption {
    label: string;
    options: Option[];
}

export interface DropdownInputProps {
    className?: string;
    ariaLabel?: string;
    defaultValue?: Option | Array<Option>;
    errorText?: string;
    errorTextClassName?: string;
    errorIconClassName?: string;
    errorClassName?: string;
    hasError?: boolean;
    helperText?: string;
    id?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    label?: string;
    name?: string;
    onChange?: (newValue: Option) => void;
    onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
    options: Array<Option> | Array<GroupedOption>;
    placeholder?: string;
    formatGroupLabel?: (data: Option) => React.ReactNode;
    value?: Option | Array<Option>;
    dataTestId?: string;
    containerClassName?: string;
    helperTextPlacement?: "before" | "after";
    showCustomErrorView?: boolean;
}

const DropdownInput = React.forwardRef<HTMLSelectElement, DropdownInputProps>((props, ref) => {
    const {
        label,
        options,
        isDisabled = false,
        isRequired = false,
        hasError = false,
        placeholder,
        id,
        defaultValue,
        onChange,
        onBlur,
        errorText,
        helperText,
        className,
        value,
        ariaLabel,
        dataTestId,
        containerClassName,
        helperTextPlacement = "after",
        showCustomErrorView,
        errorClassName,
        errorTextClassName,
        errorIconClassName,
    } = props;

    const handleChange = (newValue: Option) => {
        onChange?.(newValue);
    };

    const Label = !label ? "div" : "label";
    const showHelperText = helperText && !hasError;

    let ErrorView;

    if (hasError) {
        ErrorView = showCustomErrorView && (
            <DefaultErrorView
                errorText={errorText}
                errorClassName={errorClassName}
                errorTextClassName={errorTextClassName}
                errorIconClassName={errorIconClassName}
            />
        );
    }

    return (
        <Label className={cx("container", containerClassName)}>
            {label && <span className={cx("label", { isRequired })}>{label}</span>}

            <div className={cx("dropdown")}>
                {showHelperText && helperTextPlacement === "before" && (
                    <span className={cx("helperText")}>{helperText}</span>
                )}
                <div>
                    <UIIcon
                        color="#666666"
                        className={cx("nativeDropdownIndicator")}
                        icon="ChevronDown"
                    />
                    <select
                        data-testid={dataTestId}
                        value={value && !Array.isArray(value) ? value?.value : ""}
                        ref={ref}
                        id={id}
                        disabled={isDisabled}
                        className={cx("nativeSelect", className)}
                        aria-label={ariaLabel}
                        onChange={(e) => {
                            handleChange({ value: e.target.value, label: e.target.value });
                        }}
                        onBlur={onBlur}
                    >
                        {options.map((option) => {
                            return "options" in option ? (
                                <optgroup key={option.label} label={option.label}>
                                    {option.options.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </optgroup>
                            ) : (
                                <option key={option.label} value={option.value}>
                                    {option.label}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>

            {showHelperText && helperTextPlacement === "after" && (
                <span className={cx("helperText")}>{helperText}</span>
            )}

            {hasError && showCustomErrorView && (
                <DefaultErrorView
                    errorText={errorText}
                    errorClassName={errorClassName}
                    errorTextClassName={errorTextClassName}
                    errorIconClassName={errorIconClassName}
                />
            )}

            {hasError && !showCustomErrorView && (
                <span className={cx("errorText")} role="alert">
                    {errorText}
                </span>
            )}
        </Label>
    );
});

DropdownInput.displayName = "DropdownInput";

export default DropdownInput;
